import { LogService } from 'library/ts/utilities/logger/LogService';
import { EncodingScheme } from 'library/ts/utilities/modules/enums/EncodingScheme';
import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';

const logger = LogService.create( { prefixes: [ 'SmartTag:' ] } );

/**
 * @description Converts string to Boolean. If no match found returns original string
 * @param str {any}
 * @param defaultValue {Boolean} default value to return if str is undefined
 * @default false
 * @return {Boolean}
 */
export const stringToBoolean = ( str: any, defaultValue: boolean = false ): boolean => {
	// if str is undefined return defaultValue
	if ( str === undefined || str === null || str === '' ) {
		return defaultValue;
	}

	// sanity check for boolean passed instead of the string
	if ( typeof ( str ) === TrueType.BOOLEAN ) {
		return str;
	}

	// convert incoming value to string type
	str = `${str}`;

	// check for matching string value + return result
	if ( str.toLowerCase().match( /^(true|yes|y|1)$/ ) ) {
		return true;
	} else if ( str.toLowerCase().match( /^(false|no|n|0)$/ ) ) {
		return false;
	}

	// otherwise, return default value
	return defaultValue;
};

/**
 * @description Converts a time string hour:min:sec or min:sec into seconds
 * @param str {String} - hour:min:sec / min:sec / sec
 * @return {Number}
 */
export const stringToSeconds = ( str: string ): number => {
	const tmp: string[] = str.split( ':' );
	switch ( tmp.length ) {
		case 1: // sec only
			return parseInt( tmp[ 0 ] );
		case 2: // min:sec
			return ( parseInt( tmp[ 0 ] ) * 60 ) + parseInt( tmp[ 1 ] );
		case 3: // hour:min:sec
			return ( ( parseInt( tmp[ 0 ] ) * 60 ) * 60 ) + ( parseInt( tmp[ 1 ] ) * 60 ) + parseInt( tmp[ 2 ] );
		default:
			return 0;
	}
};

/**
 * @description Parses xml into json
 * @param xml {XMLDocument|String}
 * @return {JSON}
 */
export const xmlToJson = ( xml: XMLDocument | string | any ): JSON | any => {
	/**
	 * @description Removes line breaks + Trims string with RegEx
	 * @param x {String}
	 * @return {String|void}
	 */
	const stringTrim = ( x ) => {
		x = x.replace( /\r?\n|\r/g, '' ); // remove line breaks
		x = x.replace( /^\s+|\s+$/gm, '' ); // trim
		return x;
	};

	// Create the return object
	let obj = {};

	if ( xml.nodeType === 1 ) { // element
		// do attributes
		if ( xml.attributes.length > 0 ) {
			obj[ '@attributes' ] = {};
			for ( let j = 0; j < xml.attributes.length; j++ ) {
				const attribute = xml.attributes.item( j );
				obj[ '@attributes' ][ attribute.nodeName ] = stringTrim( attribute.nodeValue );
			}
		}
	} else if ( xml.nodeType === 3 ) { // text
		obj = stringTrim( xml.nodeValue );
	}

	// do children
	// If just one text node inside
	if ( xml.hasChildNodes() && xml.childNodes.length === 1 && xml.childNodes[ 0 ].nodeType === 3 ) {
		obj = stringTrim( xml.childNodes[ 0 ].nodeValue );
	} else if ( xml.hasChildNodes() ) {
		for ( let i = 0; i < xml.childNodes.length; i++ ) {
			const item = xml.childNodes.item( i );
			const nodeName = item.nodeName;
			if ( typeof ( obj[ nodeName ] ) === TrueType.UNDEFINED ) {
				obj[ nodeName ] = xmlToJson( item );
			} else {
				if ( typeof ( obj[ nodeName ].push ) === TrueType.UNDEFINED ) {
					const old = obj[ nodeName ];
					obj[ nodeName ] = [];
					obj[ nodeName ].push( old );
				}
				obj[ nodeName ].push( xmlToJson( item ) );
			}
		}
	}
	return obj;
};

/**
 * @description Parses XML string to XML
 * @param xmlString {String}
 * @return {XMLDocument}
 */
export const stringToXML = ( xmlString: string ): XMLDocument => {
	let parser;
	let xmlDoc;
	if ( window.DOMParser ) {
		parser = new DOMParser();
		xmlDoc = parser.parseFromString( xmlString, 'text/xml' );
	} else { // Internet Explorer
		xmlDoc = new ActiveXObject( 'Microsoft.XMLDOM' );
		xmlDoc.loadXML( xmlString );
	}
	return xmlDoc;
};

/**
 * @description Converts opacity value parsed from CSS i.e. 0.3 to HEX 4C
 * @param opacity {Number}
 * @return {String}
 */
export const opacityToHex = ( opacity: number ): string => {
	// sanity check
	if ( opacity === undefined ) opacity = 1;
	// convert to hex
	let output = Math.floor( opacity * 255 ).toString( 16 );
	// correct with 0 padding (hex should be two characters long)
	if ( output.length % 2 ) output = '0' + output;
	return output;
};

/**
 * @description Converts hex string and opacity value parsed from CSS i.e. #FFEE00 + 0.3 to '#RRGGBBAA' string
 * @param hexStr {String} HEX color string '#FFEE00'
 * @param opacity {Number} NUmber 0-1
 * @return {String}
 */
export const rgbaConcatAllInOne = ( hexStr: string, opacity: number ): string => {
	return ( hexStr + opacityToHex( opacity ) ).toUpperCase();
};

/**
 * @description Converts HEX + alpha to rgba
 * @param {string} hex
 * @param {number} alpha
 * @return {string}
 */
export const hexToRgb = ( hex: string, alpha: number ): string => {
	hex = hex.replace( '#', '' );
	const r = parseInt( hex.length === 3 ? hex.slice( 0, 1 ).repeat( 2 ) : hex.slice( 0, 2 ), 16 );
	const g = parseInt( hex.length === 3 ? hex.slice( 1, 2 ).repeat( 2 ) : hex.slice( 2, 4 ), 16 );
	const b = parseInt( hex.length === 3 ? hex.slice( 2, 3 ).repeat( 2 ) : hex.slice( 4, 6 ), 16 );
	if ( alpha ) {
		return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')';
	} else {
		return 'rgb(' + r + ', ' + g + ', ' + b + ')';
	}
};

/**
 * @description Converts html color name to HEX string
 * @public
 * @param colour {String}
 * @return {String}
 */
export const colorNameToHex = ( colour: string ): string => {
	const colours = {
		'aliceblue': '#f0f8ff',
		'antiquewhite': '#faebd7',
		'aqua': '#00ffff',
		'aquamarine': '#7fffd4',
		'azure': '#f0ffff',
		'beige': '#f5f5dc',
		'bisque': '#ffe4c4',
		'black': '#000000',
		'blanchedalmond': '#ffebcd',
		'blue': '#0000ff',
		'blueviolet': '#8a2be2',
		'brown': '#a52a2a',
		'burlywood': '#deb887',
		'cadetblue': '#5f9ea0',
		'chartreuse': '#7fff00',
		'chocolate': '#d2691e',
		'coral': '#ff7f50',
		'cornflowerblue': '#6495ed',
		'cornsilk': '#fff8dc',
		'crimson': '#dc143c',
		'cyan': '#00ffff',
		'darkblue': '#00008b',
		'darkcyan': '#008b8b',
		'darkgoldenrod': '#b8860b',
		'darkgray': '#a9a9a9',
		'darkgreen': '#006400',
		'darkkhaki': '#bdb76b',
		'darkmagenta': '#8b008b',
		'darkolivegreen': '#556b2f',
		'darkorange': '#ff8c00',
		'darkorchid': '#9932cc',
		'darkred': '#8b0000',
		'darksalmon': '#e9967a',
		'darkseagreen': '#8fbc8f',
		'darkslateblue': '#483d8b',
		'darkslategray': '#2f4f4f',
		'darkturquoise': '#00ced1',
		'darkviolet': '#9400d3',
		'deeppink': '#ff1493',
		'deepskyblue': '#00bfff',
		'dimgray': '#696969',
		'dodgerblue': '#1e90ff',
		'firebrick': '#b22222',
		'floralwhite': '#fffaf0',
		'forestgreen': '#228b22',
		'fuchsia': '#ff00ff',
		'gainsboro': '#dcdcdc',
		'ghostwhite': '#f8f8ff',
		'gold': '#ffd700',
		'goldenrod': '#daa520',
		'gray': '#808080',
		'green': '#008000',
		'greenyellow': '#adff2f',
		'honeydew': '#f0fff0',
		'hotpink': '#ff69b4',
		'indianred ': '#cd5c5c',
		'indigo': '#4b0082',
		'ivory': '#fffff0',
		'khaki': '#f0e68c',
		'lavender': '#e6e6fa',
		'lavenderblush': '#fff0f5',
		'lawngreen': '#7cfc00',
		'lemonchiffon': '#fffacd',
		'lightblue': '#add8e6',
		'lightcoral': '#f08080',
		'lightcyan': '#e0ffff',
		'lightgoldenrodyellow': '#fafad2',
		'lightgrey': '#d3d3d3',
		'lightgreen': '#90ee90',
		'lightpink': '#ffb6c1',
		'lightsalmon': '#ffa07a',
		'lightseagreen': '#20b2aa',
		'lightskyblue': '#87cefa',
		'lightslategray': '#778899',
		'lightsteelblue': '#b0c4de',
		'lightyellow': '#ffffe0',
		'lime': '#00ff00',
		'limegreen': '#32cd32',
		'linen': '#faf0e6',
		'magenta': '#ff00ff',
		'maroon': '#800000',
		'mediumaquamarine': '#66cdaa',
		'mediumblue': '#0000cd',
		'mediumorchid': '#ba55d3',
		'mediumpurple': '#9370d8',
		'mediumseagreen': '#3cb371',
		'mediumslateblue': '#7b68ee',
		'mediumspringgreen': '#00fa9a',
		'mediumturquoise': '#48d1cc',
		'mediumvioletred': '#c71585',
		'midnightblue': '#191970',
		'mintcream': '#f5fffa',
		'mistyrose': '#ffe4e1',
		'moccasin': '#ffe4b5',
		'navajowhite': '#ffdead',
		'navy': '#000080',
		'oldlace': '#fdf5e6',
		'olive': '#808000',
		'olivedrab': '#6b8e23',
		'orange': '#ffa500',
		'orangered': '#ff4500',
		'orchid': '#da70d6',
		'palegoldenrod': '#eee8aa',
		'palegreen': '#98fb98',
		'paleturquoise': '#afeeee',
		'palevioletred': '#d87093',
		'papayawhip': '#ffefd5',
		'peachpuff': '#ffdab9',
		'peru': '#cd853f',
		'pink': '#ffc0cb',
		'plum': '#dda0dd',
		'powderblue': '#b0e0e6',
		'purple': '#800080',
		'red': '#ff0000',
		'rosybrown': '#bc8f8f',
		'royalblue': '#4169e1',
		'saddlebrown': '#8b4513',
		'salmon': '#fa8072',
		'sandybrown': '#f4a460',
		'seagreen': '#2e8b57',
		'seashell': '#fff5ee',
		'sienna': '#a0522d',
		'silver': '#c0c0c0',
		'skyblue': '#87ceeb',
		'slateblue': '#6a5acd',
		'slategray': '#708090',
		'snow': '#fffafa',
		'springgreen': '#00ff7f',
		'steelblue': '#4682b4',
		'tan': '#d2b48c',
		'teal': '#008080',
		'thistle': '#d8bfd8',
		'tomato': '#ff6347',
		'turquoise': '#40e0d0',
		'violet': '#ee82ee',
		'wheat': '#f5deb3',
		'white': '#ffffff',
		'whitesmoke': '#f5f5f5',
		'yellow': '#ffff00',
		'yellowgreen': '#9acd32'
	};

	if ( typeof colours[ colour.toLowerCase() ] !== TrueType.UNDEFINED ) {
		return colours[ colour.toLowerCase() ].toUpperCase();
	}

	return null;
};

/**
 * @description Encode object to base64 string
 * @param {any} object
 * @param {EncodingScheme} type
 * @returns {string}
 */
export const objectEncode = ( object: any, type: EncodingScheme = EncodingScheme.BASE_64 ): string => {
	switch ( type ) {
		case EncodingScheme.BASE_64:
			return encodeURI( window.btoa( JSON.stringify( object ) ) ); // encode data object as base-64 string
		default:
			return object;
	}
};

/**
 * @description Decode base64 encoded object
 * @param {string} encoded
 * @param {EncodingScheme} type
 * @returns {any}
 */
export const objectDecode = ( encoded: string, type: EncodingScheme = EncodingScheme.BASE_64 ): any => {
	switch ( type ) {
		case EncodingScheme.BASE_64:
			return JSON.parse( window.atob( decodeURI( encoded ) ) ); // decode base64 encoded object
		default:
			return encoded;
	}
};

/**
 * @description Escapes the string to make it compatible with RegEx
 * @param {string} string
 * @return {string}
 */
export const escapeRegex = ( string: string = '' ): string => {
	return string.replace( /[-\/\\^$*+?.()|[\]{}]/g, '\\$&' );
};

/**
 * @description Extracts specified attributes from each of a list of Elements (such as returned by querySelectorAll) and collects them into one set of key+value pairs
 * @param {NodeListOf<Element>} elements
 * @param {string} keyAttribute
 * @param {string} valueAttribute
 * @returns {object}
 */
export const fromQueryToObject = ( elements: NodeListOf<Element>, keyAttribute: string, valueAttribute: string ): object => {
	if ( !elements || !keyAttribute || !valueAttribute ) {
		logger.warn( 'fromQueryToObject > all parameters must be supplied.' );
		return {};
	}

	// PLBCK-5070: Replace Object.fromEntries with ES5-compatible code for Samsung Tizen support
	const result: Record<string, string> = {};
	elements.forEach( ( nodeListElement ) => {
		if ( nodeListElement.hasAttribute( keyAttribute ) && nodeListElement.hasAttribute( valueAttribute ) ) {
			const key = nodeListElement.getAttribute( keyAttribute );
			const value = nodeListElement.getAttribute( valueAttribute );
			if ( key ) {
				result[ key ] = value || '';
			}
		}
	} );
	return result;
};

/**
 * @description Extracts the attributes of an element into key+value pairs
 * @param {Element} e
 * @returns {object}
 */
export const fromElementToObject = ( e: Element ): object => {
	if ( !e ) {
		logger.warn( 'fromElementToObject > element must not be empty.' );
		return {};
	}

	const attributes: NamedNodeMap = e.attributes;
	const out: object = {};
	for ( let i = 0, l = attributes.length; i < l; ++i ) {
		const attribute: Attr = attributes.item( i );
		out[ attribute.name ] = attribute.value;
	}
	return out;
};